/**
 * My Yacht Page
 * View and edit yacht profile (one yacht per captain)
 * Updated: v2 with photo upload
 */

import React, { useState, useEffect, useRef } from 'react';
import { yachtsAPI } from '../../services/api';
import { useNotifications } from '../../context/NotificationContext';
import {
  Ship,
  Edit2,
  X,
  Check,
  Anchor,
  Camera,
  Upload,
  Trash2,
  Image,
} from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
const UPLOADS_BASE_URL = API_BASE_URL.replace(/\/api\/?$/, '');

function MyYachts() {
  const { showToast } = useNotifications();
  const [yacht, setYacht] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [deletingPhoto, setDeletingPhoto] = useState(null);
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    name: '',
    yacht_type: 'motor',
    length: '',
  });

  const [photos, setPhotos] = useState([]);

  useEffect(() => {
    loadYacht();
  }, []);

  const loadYacht = async () => {
    try {
      const data = await yachtsAPI.getMyYachts();
      // Get the first (and only) yacht
      const userYacht = data.yachts?.[0] || data.data?.[0] || data[0] || null;
      setYacht(userYacht);
      if (userYacht) {
        setFormData({
          name: userYacht.name || '',
          yacht_type: userYacht.yacht_type || 'motor',
          length: userYacht.length_m?.toString() || userYacht.length?.toString() || '',
        });
        setPhotos(userYacht.photos || []);
      }
    } catch (error) {
      console.error('Failed to load yacht:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);

    try {
      const yachtData = {
        name: formData.name,
        yacht_type: formData.yacht_type,
        length_m: parseFloat(formData.length),
      };

      if (yacht) {
        // Update existing yacht
        await yachtsAPI.updateYacht(yacht.id, yachtData);
        showToast('Yacht updated successfully', 'success');
      } else {
        // Create new yacht
        const response = await yachtsAPI.createYacht(yachtData);
        const newYacht = response.data || response.yacht;
        setYacht(newYacht);
        showToast('Yacht created! You can now add photos.', 'success');
      }
      setShowForm(false);
      await loadYacht();
    } catch (error) {
      showToast(error.message || 'Failed to save yacht', 'error');
    } finally {
      setFormLoading(false);
    }
  };

  const closeForm = () => {
    setShowForm(false);
    // Reset form to current yacht data
    if (yacht) {
      setFormData({
        name: yacht.name || '',
        yacht_type: yacht.yacht_type || 'motor',
        length: yacht.length_m?.toString() || yacht.length?.toString() || '',
      });
      setPhotos(yacht.photos || []);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      showToast('Please select an image file', 'error');
      return;
    }

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      showToast('Image must be less than 10MB', 'error');
      return;
    }

    setUploadingPhoto(true);
    try {
      const response = await yachtsAPI.uploadPhoto(yacht.id, file);
      setPhotos(response.data?.photos || [...photos, response.data?.url]);
      showToast('Photo uploaded successfully', 'success');
    } catch (error) {
      showToast(error.message || 'Failed to upload photo', 'error');
    } finally {
      setUploadingPhoto(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeletePhoto = async (photoUrl) => {
    if (!window.confirm('Remove this photo?')) return;

    setDeletingPhoto(photoUrl);
    try {
      const response = await yachtsAPI.deletePhoto(yacht.id, photoUrl);
      setPhotos(response.data?.photos || photos.filter(p => p !== photoUrl));
      showToast('Photo removed', 'success');
    } catch (error) {
      showToast(error.message || 'Failed to remove photo', 'error');
    } finally {
      setDeletingPhoto(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  if (!yacht && !showForm) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="card text-center py-12">
          <Ship className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-navy-900 mb-2">
            No yacht registered
          </h3>
          <p className="text-navy-600 mb-6">
            Add your yacht to start requesting mooring assistance.
          </p>
          <button
            onClick={() => setShowForm(true)}
            className="btn-primary"
          >
            Add My Yacht
          </button>
        </div>
      </div>
    );
  }

  if (!yacht && showForm) {
    return (
      <div className="max-w-2xl mx-auto space-y-6">
        <div>
          <h1 className="text-2xl font-display font-bold text-navy-900">
            Add My Yacht
          </h1>
          <p className="text-navy-600 mt-1">
            Enter your vessel details
          </p>
        </div>
        <div className="card">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-navy-700 mb-1">
                Yacht Name
              </label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="input"
                placeholder="e.g., Sea Breeze"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-navy-700 mb-1">
                Type
              </label>
              <select
                name="yacht_type"
                value={formData.yacht_type}
                onChange={handleChange}
                className="input"
              >
                <option value="motor">Motor Yacht</option>
                <option value="sailing">Sailing Yacht</option>
                <option value="catamaran">Catamaran</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-navy-700 mb-1">
                Length (meters)
              </label>
              <input
                type="number"
                name="length"
                value={formData.length}
                onChange={handleChange}
                className="input"
                placeholder="e.g., 15"
                step="0.1"
                min="1"
                required
              />
            </div>
            <p className="text-sm text-navy-500 pt-2">
              You can add photos after creating your yacht.
            </p>
            <div className="flex space-x-3 pt-4">
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="btn-secondary flex-1"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={formLoading}
                className="btn-primary flex-1"
              >
                {formLoading ? 'Saving...' : 'Add Yacht'}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-display font-bold text-navy-900">
          My Yacht
        </h1>
        <p className="text-navy-600 mt-1">
          Your vessel details
        </p>
      </div>

      {/* Yacht Card */}
      <div className="card">
        <div className="flex items-start justify-between">
          <div className="flex items-start">
            {/* Show yacht photo or default icon */}
            <div className="h-20 w-20 bg-primary-100 rounded-xl flex items-center justify-center overflow-hidden flex-shrink-0">
              {photos.length > 0 ? (
                <img
                  src={photos[0].startsWith('http') ? photos[0] : `${UPLOADS_BASE_URL}${photos[0]}`}
                  alt={yacht.name}
                  className="h-20 w-20 object-cover"
                />
              ) : (
                <Ship className="h-10 w-10 text-primary-600" />
              )}
            </div>
            <div className="ml-5">
              <h2 className="text-xl font-semibold text-navy-900">
                {yacht.name}
              </h2>
              <p className="text-navy-600 capitalize mt-1">
                {yacht.yacht_type} Yacht
              </p>
              <div className="mt-3 flex items-center text-navy-500">
                <Anchor className="h-4 w-4 mr-2" />
                <span>{yacht.length_m || yacht.length}m length</span>
              </div>
            </div>
          </div>

          <button
            onClick={() => setShowForm(true)}
            className="btn-secondary"
          >
            <Edit2 className="h-4 w-4 mr-2" />
            Edit
          </button>
        </div>

        {/* Photo Gallery */}
        {photos.length > 1 && (
          <div className="mt-6 pt-6 border-t border-gray-100">
            <p className="text-sm font-medium text-navy-500 mb-3">Photos ({photos.length})</p>
            <div className="flex gap-3 overflow-x-auto pb-2">
              {photos.map((photo, idx) => (
                <div key={idx} className="h-24 w-24 flex-shrink-0 rounded-lg overflow-hidden">
                  <img
                    src={photo.startsWith('http') ? photo : `${UPLOADS_BASE_URL}${photo}`}
                    alt={`${yacht.name} ${idx + 1}`}
                    className="h-full w-full object-cover"
                  />
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Edit Yacht Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-display font-semibold text-navy-900">
                Edit Yacht
              </h2>
              <button
                onClick={closeForm}
                className="p-2 text-navy-400 hover:text-navy-600 hover:bg-gray-100 rounded-lg"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              <div>
                <label className="block text-sm font-medium text-navy-700 mb-2">
                  Yacht Name
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="input-field"
                  placeholder="e.g., Ocean Dream"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-navy-700 mb-2">
                  Yacht Type
                </label>
                <select
                  name="yacht_type"
                  value={formData.yacht_type}
                  onChange={handleChange}
                  className="input-field"
                >
                  <option value="motor">Motor</option>
                  <option value="sailing">Sailing</option>
                  <option value="catamaran">Catamaran</option>
                  <option value="superyacht">Superyacht</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-navy-700 mb-2">
                  Length (meters)
                </label>
                <input
                  type="number"
                  name="length"
                  value={formData.length}
                  onChange={handleChange}
                  className="input-field"
                  placeholder="12"
                  step="0.1"
                  min="1"
                  required
                />
              </div>

              {/* Photo Upload Section */}
              <div>
                <label className="block text-sm font-medium text-navy-700 mb-2">
                  Photos
                </label>

                {/* Existing Photos */}
                {photos.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-3">
                    {photos.map((photo, idx) => (
                      <div key={idx} className="relative group">
                        <div className="h-20 w-20 rounded-lg overflow-hidden">
                          <img
                            src={photo.startsWith('http') ? photo : `${UPLOADS_BASE_URL}${photo}`}
                            alt={`Yacht ${idx + 1}`}
                            className="h-full w-full object-cover"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => handleDeletePhoto(photo)}
                          disabled={deletingPhoto === photo}
                          className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                        >
                          {deletingPhoto === photo ? (
                            <span className="spinner h-3 w-3" />
                          ) : (
                            <X className="h-3 w-3" />
                          )}
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Upload Button */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handlePhotoUpload}
                  className="hidden"
                  id="yacht-photo-input"
                />
                <label
                  htmlFor="yacht-photo-input"
                  className={`inline-flex items-center px-4 py-2 border border-dashed border-navy-300 rounded-lg text-sm text-navy-600 hover:border-primary-500 hover:text-primary-600 cursor-pointer transition-colors ${uploadingPhoto ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  {uploadingPhoto ? (
                    <>
                      <span className="spinner h-4 w-4 mr-2" />
                      Uploading...
                    </>
                  ) : (
                    <>
                      <Camera className="h-4 w-4 mr-2" />
                      Add Photo
                    </>
                  )}
                </label>
                <p className="text-xs text-navy-400 mt-2">Max 10MB per photo. JPG, PNG supported.</p>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={closeForm}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={formLoading}
                  className="btn-primary"
                >
                  {formLoading ? (
                    <span className="flex items-center">
                      <span className="spinner h-5 w-5 mr-2" />
                      Saving...
                    </span>
                  ) : (
                    <>
                      <Check className="h-5 w-5 mr-2" />
                      Save Changes
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default MyYachts;
